import { DataGridErrorType } from '@/typing/enums/dataGridErrorType.enum';
import { isUndefined } from '@/utils/isUndefined';
import { DataGridColumnType } from './DataGridColumnType.class';

export class DataGridColumn implements IDataGridColumn {
    header: string;
    valueKey: string;
    validatorFunc?: (
        value: any,
        item?: any,
        items?: any[],
        column?: DataGridColumn,
        columns?: DataGridColumn[]
    ) => IDataGridColumnValidatorResult;
    valueWhenBlurOnEmptyFunc?: (item: any) => any;
    maxWidthCSS?: string;
    minWidthCSS?: string;
    additionalInputType?: DataGridColumnType;
    selectOptions?: any[];
    selectOptionLabelFn: (obj: any) => string;
    selectOptionValueFn: (obj: any) => any;
    isIconColumn?: boolean;
    iconNameFn?: (item?: any) => string;
    iconColorHexFn?: (item?: any) => string;
    iconTitleFn?: (item?: any) => string | null;
    showPlaceholderValue?: boolean;
    showAllSelectOptions?: boolean;
    isEditable?: boolean;
    group?: string;
    required?: boolean;
    useSelectPossibilitiesWhenUndefined?: boolean;
    autoComplete!: boolean;
    dateValueKey?: string;
    dateUndefined?: (item: any, column: DataGridColumn, columns: DataGridColumn[]) => any;

    constructor(m: IDataGridColumn) {
        this.header = m.header ?? '';
        this.valueKey = m.valueKey ?? '';
        this.validatorFunc = (m.validatorFunc as any) ?? undefined;
        this.valueWhenBlurOnEmptyFunc = m.valueWhenBlurOnEmptyFunc ?? undefined;
        this.maxWidthCSS = m.maxWidthCSS ?? 'unset';
        this.minWidthCSS = m.minWidthCSS ?? 'unset';
        this.additionalInputType = m.additionalInputType ?? undefined;
        this.selectOptions = m.selectOptions ?? undefined;
        this.selectOptionLabelFn =
            m.selectOptionLabelFn ?? ((obj: any) => obj.toString());
        this.selectOptionValueFn = m.selectOptionValueFn ?? ((obj: any) => obj);
        this.isIconColumn = m.isIconColumn ?? false;
        this.iconNameFn = m.iconNameFn ?? undefined;
        this.iconTitleFn = m.iconTitleFn ?? undefined;
        this.iconColorHexFn = m.iconColorHexFn ?? undefined;
        this.showPlaceholderValue = m.showPlaceholderValue ?? true;
        this.showAllSelectOptions = m.showAllSelectOptions ?? false;
        this.isEditable = m.isEditable ?? true;
        this.group = m.group ?? undefined;
        this.required = m.required ?? false;
        this.useSelectPossibilitiesWhenUndefined =
            m.useSelectPossibilitiesWhenUndefined ?? false;
        this.autoComplete = m.autoComplete ?? false;
        this.dateValueKey = m.dateValueKey ?? undefined;
        this.dateUndefined = m.dateUndefined ?? undefined;
    }

    public getUndefinedValue(
        item: any,
        selectOptions: any
    ): { value: any; usedSelectionOption: any } {
        if (
            selectOptions &&
            selectOptions.length > 0 &&
            this.useSelectPossibilitiesWhenUndefined
        ) {
            const selectValue = selectOptions[0];

            if (typeof selectValue === 'object') {
                return {
                    value: this.selectOptionValueFn(selectValue),
                    usedSelectionOption: selectValue,
                };
            }

            return { value: selectValue, usedSelectionOption: null };
        }

        if (this.valueWhenBlurOnEmptyFunc) {
            return {
                value: this.valueWhenBlurOnEmptyFunc(item),
                usedSelectionOption: null,
            };
        }

        return { value: null, usedSelectionOption: null };
    }

    public validate(item: any, items: any[], columns: DataGridColumn[]): IDataGridColumnValidatorResult {
        const defaultResult = { hasError: false, message: undefined };

        if (!this.validatorFunc && !this.required) return defaultResult;

        const value = item[this.valueKey];

        if (isUndefined(value) && this.required) {
            return {
                hasError: true,
                message: 'is een verplicht veld',
                errorType: DataGridErrorType.RequiredError,
            };
        }

        if (!this.validatorFunc) return defaultResult;

        const result = this.validatorFunc(value, item, items, this, columns);

        if (result && result.hasError) {
            return {
                hasError: result.hasError,
                message: result.message,
                errorType: result.errorType,
            };
        }

        return defaultResult;
    }
}

interface IDataGridColumn {
    header: string;
    valueKey: string;
    validatorFunc?: (
        val: any,
        item: any,
        items: any[],
        column: DataGridColumn,
        columns: DataGridColumn[]
    ) => IDataGridColumnValidatorResult;
    valueWhenBlurOnEmptyFunc?: (item: any) => any;
    maxWidthCSS?: string;
    minWidthCSS?: string;
    additionalInputType?: DataGridColumnType;
    selectOptions?: any[];
    selectOptionLabelFn?: (obj: any) => string;
    selectOptionValueFn?: (obj: any) => any;
    isIconColumn?: boolean;
    iconNameFn?: (item?: any) => string;
    iconTitleFn?: (item?: any) => string | null;
    iconColorHexFn?: (item?: any) => string;
    showPlaceholderValue?: boolean;
    showAllSelectOptions?: boolean;
    isEditable?: boolean;
    group?: string;
    required?: boolean;
    useSelectPossibilitiesWhenUndefined?: boolean;
    autoComplete?: boolean;
    dateValueKey?: string;
    dateUndefined?: (item: any, column: DataGridColumn, columns: DataGridColumn[]) => any;
}

export interface IDataGridColumnValidatorResult {
    hasError: boolean;
    message?: string;
    errorType?:
        | DataGridErrorType.Error
        | DataGridErrorType.Warning
        | DataGridErrorType.RequiredError;
    columnIndex?: number;
    itemIndex?: number;
}

export function dataGridValidationResultIsError(
    e: IDataGridColumnValidatorResult
) {
    return (
        !e.errorType || (e.errorType && e.errorType === DataGridErrorType.Error)
    );
}

export function dataGridValidationResultIsRequired(
    e: IDataGridColumnValidatorResult
) {
    return (
        !e.errorType ||
        (e.errorType && e.errorType === DataGridErrorType.RequiredError)
    );
}
